import { z } from "zod";
import { IsoDateTime, OperatorEnvironment, Product } from "./common.ts";
import { SessionTokenClaims } from "./token.ts";
import { BrowserGuardSignals, GlvSignals, PrecheckSignals } from "./verify.ts";

// Event Gateway → Decision Engine DTOs v1 (change split-core-into-trust-contexts, design D2, D3):
// what the Event Gateway hands to the Decision Engine once it admitted an Interaction (session
// token, mode, revocation, origin, signal schema, freshness, nonce and rate limit passed), and the
// rejection it asks the Decision Engine to audit. In process today, serializable and strict (ADR
// 0001 R5). The signals cross as the SDK sent them (validated against the schema of the GeoTrust
// option): their precise coordinates only go on towards Location, which alone keeps them.

/**
 * GeoTrust `location` option (design D3): `none` (no browser location: PreCheck), `standard`
 * (browser location with consent: Browser Guard, PLC), `high_accuracy` (a consented precise
 * location is mandatory and the purpose `other` is refused: GLV).
 */
export const GEOTRUST_LOCATION_OPTIONS = ["none", "standard", "high_accuracy"] as const;
export const GeoTrustLocationOption = z
  .enum(GEOTRUST_LOCATION_OPTIONS)
  .meta({ id: "GeoTrustLocationOption" });
export type GeoTrustLocationOption = z.infer<typeof GeoTrustLocationOption>;

/** GeoTrust `revalidation` option (design D3): `periodic` for PLC, `none` otherwise. */
export const GEOTRUST_REVALIDATION_OPTIONS = ["none", "periodic"] as const;
export const GeoTrustRevalidationOption = z
  .enum(GEOTRUST_REVALIDATION_OPTIONS)
  .meta({ id: "GeoTrustRevalidationOption" });
export type GeoTrustRevalidationOption = z.infer<typeof GeoTrustRevalidationOption>;

/**
 * The published signal schema of each GeoTrust `location` option (the schemas of
 * `SIGNALS_BY_PRODUCT`, keyed by option instead of product: `none` = PreCheck, `standard` =
 * Browser Guard and PLC, `high_accuracy` = GLV).
 */
export const SIGNALS_BY_LOCATION_OPTION = {
  none: PrecheckSignals,
  standard: BrowserGuardSignals,
  high_accuracy: GlvSignals,
} as const satisfies Record<GeoTrustLocationOption, z.ZodType>;

const admittedInteraction = <L extends GeoTrustLocationOption>(
  location: L,
  signals: (typeof SIGNALS_BY_LOCATION_OPTION)[L],
) =>
  z
    .object({
      /** The verified session token's claims (operator, environment, product, player references). */
      session: SessionTokenClaims,
      options: z
        .object({ location: z.literal(location), revalidation: GeoTrustRevalidationOption })
        .strict(),
      /** The body of `POST /v1/verify`, validated against the schema of the `location` option. */
      signals,
      /** Client IP from the trusted gateway header (never from the body); absent → none known. */
      source_ip: z.string().min(1).max(64).optional(),
      /** When the Event Gateway received the request (the Interaction's `response_time_ms` starts here). */
      received_at: IsoDateTime,
    })
    .strict();

/**
 * Event Gateway → Decision Engine (`DecisionEngine.decide`): one admitted Interaction. The Decision
 * Engine answers the `VerifyResponse` of `POST /v1/verify`, or `invalid_token` (the token's
 * operator environment is no longer active), `invalid_signals` (signals its domain refuses) or an
 * unavailable dependency (`503`, fail closed).
 */
export const AdmittedInteractionV1 = z
  .union([
    admittedInteraction("none", PrecheckSignals),
    admittedInteraction("standard", BrowserGuardSignals),
    admittedInteraction("high_accuracy", GlvSignals),
  ])
  .meta({ id: "AdmittedInteractionV1" });
export type AdmittedInteractionV1 = z.infer<typeof AdmittedInteractionV1>;

/**
 * Event Gateway → Decision Engine (`AdmissionAuditLog`): a `POST /v1/verify` refused once the
 * operator is known (the session token was verified), recorded as `verification_rejected` in the
 * Decision Engine's append-only audit, which pseudonymizes the IP (its pepper stays there). No
 * token, no player reference, no signal: only the token's `jti`, its credential for a revocation,
 * the product and the refusal code.
 */
export const AdmissionRejectionV1 = z
  .object({
    operator_id: z.uuid(),
    environment: OperatorEnvironment,
    /** The refused session token's `jti` (the audited subject). */
    jti: z.uuid(),
    product: Product,
    /** The API error code of the refusal (`token_revoked`, `origin_not_allowed`, …). */
    outcome: z.string().min(1).max(64),
    /** For `token_revoked`: the credential that obtained the token (revoked with it or not). */
    credential_id: z.uuid().optional(),
    /** Non-identifying facts of the refusal (`origin_present`, `origin_registered`). */
    details: z
      .record(z.string().max(64), z.union([z.string().max(64), z.number(), z.boolean()]))
      .optional(),
    source_ip: z.string().min(1).max(64).optional(),
    occurred_at: IsoDateTime,
  })
  .strict()
  .meta({ id: "AdmissionRejectionV1" });
export type AdmissionRejectionV1 = z.infer<typeof AdmissionRejectionV1>;
